import { lazy } from "react";

// @mui icons
import Icon from "@mui/material/Icon";

// Your custom translation hook
import { useTranslation } from "hooks/useTranslation";

// Annotation Portal React layouts
const Dashboard = lazy(() => import("layouts/dashboard"));

// Super Admin
const SuperAdminMain = lazy(() => import("layouts/super_admin/admin"));

// Admin
const AdminMain = lazy(() => import("layouts/admin/annotator"));
const AdminExams = lazy(() => import("layouts/admin/exams"));
// Unused while the Students route below is commented out.
// const AdminStudents = lazy(() => import("layouts/admin/students"));
const AdminGuidelines = lazy(() => import("layouts/admin/guidelines"));
const ViewSamplePage = lazy(() => import("layouts/admin/exams/ViewSamplePage"));
const AdminQualityCheck = lazy(() => import("layouts/admin/qualityCheck"));
const AdminEnumeratorSummary = lazy(() => import("layouts/admin/enumeratorSummary"));

// Annotator
const AnnotatorExams = lazy(() => import("layouts/annotator/exams"));
const AnnotatorGuidelines = lazy(() => import("layouts/annotator/guidelines"));

const AnnotationPage = lazy(() => import("layouts/annotator/exams/annotation"));

// Custom hook to get translated routes
const useTranslatedRoutes = () => {
  const { t } = useTranslation();

  const routes = [
    // Dashboard (Accessible to all roles)
    {
      type: "collapse",
      name: t("navigation.dashboard"),
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/dashboard",
      component: <Dashboard />,
      roles: ["SuperAdmin", "Admin", "Annotator"],
    },
    // Super Admin Routes
    {
      type: "collapse",
      name: t("navigation.users"),
      key: "SuperAdmin_main",
      icon: <Icon fontSize="small">admin_panel_settings</Icon>,
      route: "/superAdmin/admin",
      component: <SuperAdminMain />,
      roles: ["SuperAdmin"],
    },
    // Admin Routes
    {
      type: "collapse",
      name: t("navigation.annotators"),
      key: "Admin_main",
      icon: <Icon fontSize="small">record_voice_over</Icon>,
      route: "/admin/annotator",
      component: <AdminMain />,
      roles: ["Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.exams"),
      key: "Admin_samples",
      icon: <Icon fontSize="small">graphic_eq</Icon>,
      route: "/admin/exams",
      component: <AdminExams />,
      roles: ["SuperAdmin", "Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.exams"),
      key: "Annotator_exams",
      icon: <Icon fontSize="small">graphic_eq</Icon>,
      route: "/annotator/exams",
      component: <AnnotatorExams />,
      roles: ["Annotator"],
    },
    /* Students — hidden from the sidebar. Commenting out the whole entry also
       drops the /admin/students route, so the page is unreachable by URL too;
       uncomment this block (and the AdminStudents import above) to restore it.
    {
      type: "collapse",
      name: t("navigation.students"),
      key: "Student_samples",
      icon: <Icon fontSize="small">school</Icon>,
      route: "/admin/students",
      component: <AdminStudents />,
      roles: ["SuperAdmin", "Admin"],
    }, */
    {
      type: "collapse",
      name: t("navigation.qualityCheck"),
      key: "Admin_quality_check",
      icon: <Icon fontSize="small">fact_check</Icon>,
      route: "/admin/quality-check",
      component: <AdminQualityCheck />,
      roles: ["Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.enumeratorSummary"),
      key: "Admin_enumerator_summary",
      icon: <Icon fontSize="small">summarize</Icon>,
      route: "/admin/enumerator-summary",
      component: <AdminEnumeratorSummary />,
      roles: ["Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.guidelines"),
      key: "Admin_guidelines",
      icon: <Icon fontSize="small">menu_book</Icon>,
      route: "/admin/guidelines",
      component: <AdminGuidelines />,
      roles: ["SuperAdmin", "Admin"],
    },

    // Annotator Routes
    {
      type: "collapse",
      name: t("navigation.guidelines"),
      key: "Annotator_guidelines",
      icon: <Icon fontSize="small">menu_book</Icon>,
      route: "/annotator/guidelines",
      component: <AnnotatorGuidelines />,
      roles: ["Annotator"],
    },

    // Authentication
    /*   {
      type: "collapse",
      name: t("navigation.signIn"),
      key: "sign_in",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/authentication/sign-in",
      component: <SignIn />,
      roles: ["SuperAdmin", "Admin", "Annotator"],
    } */
    // Hidden route (no sidebar item)
    {
      type: "none",
      name: t("navigation.annotation"),
      key: "annotator-exams-annotation",
      route: "/annotator/exams/annotation",
      component: <AnnotationPage />,
      noCollapse: true,
      roles: ["Annotator"],
    },
    // Hidden route — admin view sample page
    {
      type: "none",
      name: "View Sample",
      key: "admin-exams-view-sample",
      route: "/admin/exams/view-sample",
      component: <ViewSamplePage />,
      noCollapse: true,
      roles: ["SuperAdmin", "Admin"],
    },
  ];

  return routes;
};

// Export the hook for component usage
export { useTranslatedRoutes };

// Export default routes for backward compatibility (without translation)
const defaultRoutes = [
  // Dashboard (Accessible to all roles)
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    roles: ["SuperAdmin", "Admin", "Annotator"],
  },
  // Super Admin Routes
  {
    type: "collapse",
    name: "Users",
    key: "SuperAdmin_main",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/superAdmin/admin",
    component: <SuperAdminMain />,
    roles: ["SuperAdmin"],
  },
  // Admin Routes
  {
    type: "collapse",
    name: "Annotators",
    key: "Admin_main",
    icon: <Icon fontSize="small">record_voice_over</Icon>,
    route: "/admin/annotator",
    component: <AdminMain />,
    roles: ["Admin"],
  },
  {
    type: "collapse",
    name: "Samples",
    key: "Admin_samples",
    icon: <Icon fontSize="small">graphic_eq</Icon>,
    route: "/admin/exams",
    component: <AdminExams />,
    roles: ["SuperAdmin", "Admin"],
  },
  {
    type: "collapse",
    name: "Samples",
    key: "Annotator_exams",
    icon: <Icon fontSize="small">graphic_eq</Icon>,
    route: "/annotator/exams",
    component: <AnnotatorExams />,
    roles: ["Annotator"],
  },
  {
    type: "collapse",
    name: "Quality Check",
    key: "Admin_quality_check",
    icon: <Icon fontSize="small">fact_check</Icon>,
    route: "/admin/quality-check",
    component: <AdminQualityCheck />,
    roles: ["Admin"],
  },
  {
    type: "collapse",
    name: "Guidelines",
    key: "Admin_guidelines",
    icon: <Icon fontSize="small">menu_book</Icon>,
    route: "/admin/guidelines",
    component: <AdminGuidelines />,
    roles: ["SuperAdmin", "Admin"],
  },
  {
    type: "collapse",
    name: "Quality Check Summary",
    key: "Admin_enumerator_summary",
    icon: <Icon fontSize="small">summarize</Icon>,
    route: "/admin/enumerator-summary",
    component: <AdminEnumeratorSummary />,
    roles: ["Admin"],
  },

  // Annotator Routes
  {
    type: "collapse",
    name: "Guidelines",
    key: "Annotator_guidelines",
    icon: <Icon fontSize="small">menu_book</Icon>,
    route: "/annotator/guidelines",
    component: <AnnotatorGuidelines />,
    roles: ["Annotator"],
  },

  // Hidden route (no sidebar item)
  {
    type: "none",
    name: "Annotation",
    key: "annotator-exams-annotation",
    route: "/annotator/exams/annotation",
    component: <AnnotationPage />,
    noCollapse: true,
    roles: ["Annotator"],
  },
  // Hidden route — admin view sample page
  {
    type: "none",
    name: "View Sample",
    key: "admin-exams-view-sample",
    route: "/admin/exams/view-sample",
    component: <ViewSamplePage />,
    noCollapse: true,
    roles: ["SuperAdmin", "Admin"],
  },
];

export default defaultRoutes;
